import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, Card, CardService, Funnel, FunnelService, Stage, StageService } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';
import { SelectLossReasonComponent, SelectLossReasonResult } from '../loss-reason/select-loss-reason/select-loss-reason.component';
import { PresentationDateDialogComponent, PresentationDateDialogResult } from '../presentation-date-dialog/presentation-date-dialog.component';
import { StageKindEnum } from 'lib-trend-core';
import { getStages } from '../state/actions';
import { AppCRMState } from '../state/app.state';

type TransferCardData = { card: Card; idStage?: string; action: 'move' | 'clone' };

@Component({
    selector: 'app-transfer-card',
    templateUrl: './transfer-card.component.html',
    styleUrl: './transfer-card.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransferCardComponent extends AbstractComponent {
  public funnels: Funnel[] = [];
  public stages: Stage[] = [];
  public filteredStages: Stage[] = [];

  get isClone(): boolean {
    return this.data.action === 'clone';
  }

  get title(): string {
    return this.isClone ? this.t('kanban.transferCard.cloneTitle') : this.t('kanban.transferCard.moveTitle');
  }

  get confirmLabel(): string {
    return this.isClone ? this.t('kanban.transferCard.clone') : this.t('kanban.transferCard.move');
  }

  constructor(
    injector: Injector,
    private dialogRef: MatDialogRef<TransferCardComponent>,
    private store: Store<AppCRMState>,
    private cardService: CardService,
    private stageService: StageService,
    private funnelService: FunnelService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: TransferCardData,
  ) {
    super(injector);
    this.setupForm();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      funnel: [null, [Validators.required]],
      stage: [null, [Validators.required]],
    });

    this.formGroup.valueChanges.subscribe(value => {
      if (!value.funnel) {
        this.filteredStages = [];
        this.formGroup.get('stage')?.setValue(null);
      }
    });
  }

  ngOnInit(): void {
    this.getFunnels();
    this.getStages();
  }

  close(): void {
    this.dialogRef.close();
  }

  selectFunnel(funnel: Funnel): void {
    this.filteredStages = this.stages.filter(stage => this.getId(stage.funnel) === funnel._id);
    this.formGroup.get('stage')?.setValue(null);
  }

  getStages() {
    this.stageService.getList().subscribe({
      next: (stages) => {
        this.stages = stages;
        this.preselectFromCard();
      }
    });
  }

  getFunnels() {
    this.funnelService.getList().subscribe({
      next: (funnels) => {
        this.funnels = funnels;
        this.preselectFromCard();
      }
    });
  }

  confirm(): void {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    if (this.isClone) {
      this.executeClone();
      return;
    }

    this.handleMove();
  }

  private handleMove(): void {
    const { funnel, stage } = this.formGroup.getRawValue();
    const selectedStage = this.stages.find(s => s._id === stage);

    if (selectedStage?.lostDeal) {
      this.dialog.open(SelectLossReasonComponent, {
        width: '700px',
        disableClose: true,
        data: this.data.card
      })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: SelectLossReasonResult) => {
          if (!result?.idLossReason) return;
          // The reason has to travel WITH the move: the API refuses a lost
          // destination without one (RF-29) and nothing else persists it. The note
          // travels the same way for a platform discard (E3 RF-24).
          this.executeMove(funnel, stage, result.idLossReason, undefined, result.lossNote);
        });
      return;
    }

    // A presentation stage offers the presentation date (E2 RF-15, optional since
    // 2026-09-16): asked here, sent with the move when given; skipping still moves.
    if (selectedStage?.kind === StageKindEnum.PRESENTATION && !this.data?.card?.presentedAt) {
      this.dialog.open(PresentationDateDialogComponent, { width: '560px', disableClose: true, data: { card: this.data.card } })
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: PresentationDateDialogResult) => {
          if (!result) return;
          this.executeMove(funnel, stage, undefined, 'presentedAt' in result ? result.presentedAt : undefined);
        });
      return;
    }

    this.executeMove(funnel, stage);
  }

  private executeMove(funnel: string, stage: string, idLossReason?: string, presentedAt?: string, lossNote?: string): void {
    const cardId = this.data?.card?._id;
    const originFunnelId = this.getId(this.data.card.funnel);

    this.loading = true;

    this.cardService.moveCardToFunnelAndStage(cardId, funnel, stage, idLossReason, presentedAt, lossNote)
      .pipe(
        finalize(() => this.loading = false),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          if (originFunnelId) {
            this.store.dispatch(getStages({ idFunnel: originFunnelId }));
          }
          this.dialogRef.close({ moved: true });
        },
        // A refused move left the dialog open with no word to the user, so the
        // move looked like it had simply been ignored.
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('kanban.transferCard.moveError')),
      });
  }

  private executeClone(): void {
    const { funnel, stage } = this.formGroup.getRawValue();
    const cardId = this.data?.card?._id;
    const originFunnelId = this.getId(this.data.card.funnel);

    this.loading = true;
    this.cardService.cloneCardToFunnelAndStage(cardId, funnel, stage)
      .pipe(finalize(() => this.loading = false), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (originFunnelId) {
            this.store.dispatch(getStages({ idFunnel: originFunnelId }));
          }
          this.dialogRef.close({ cloned: true });
        },
      });
  }

  private preselectFromCard(): void {
    if (!this.data?.card || !this.funnels.length || !this.stages.length) return;

    const funnelId = this.getId(this.data.card.funnel);
    const stageId = this.getId(this.data.card.stage);
    if (!funnelId || !stageId) return;

    const funnel = this.funnels.find(f => f._id === funnelId);
    if (!funnel) return;

    this.formGroup.get('funnel')?.setValue(funnelId);
    this.selectFunnel(funnel);
    this.formGroup.get('stage')?.setValue(stageId);
  }

  private getId(value: string | { _id?: string } | null | undefined): string | null {
    return typeof value === 'string' ? value : value?._id ?? null;
  }
}
